/**
 * Strategies.
 *
 * The screen exists to make one question answerable: is this setup still
 * working? So the list is ranked WORST FIRST, and a strategy whose sample has
 * settled and whose expectancy is negative is flagged rather than left for the
 * trader to notice.
 *
 * The flag is an observation, never an instruction. AEGIS cannot evaluate a
 * sentence like "stops working if the opening range stops holding" — that is
 * the trader's judgement — so the screen puts the strategy's own invalidation
 * next to the number and lets them make the call.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import type { StrategiesResponse, StrategyDto, StrategyInput } from '@aegis/api-client';
import { api } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';

const pct = (s: string): string => `${(Number(s) * 100).toFixed(1)}%`;
const money = (s: string): string => {
  const n = Number(s);
  return `${n < 0 ? '−' : n > 0 ? '+' : ''}$${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const BLANK: StrategyInput = {
  name: '', invalidation: '', hypothesis: '', entryRules: '', exitRules: '',
  status: 'testing', minSample: 30,
};

export function StrategiesScreen(): JSX.Element {
  const [data, setData] = useState<StrategiesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<StrategyInput | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.strategies()); setError(null); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not load strategies.'); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async (): Promise<void> => {
    if (!draft) return;
    setBusy(true);
    try {
      if (editing) await api.updateStrategy(editing, draft);
      else await api.createStrategy(draft);
      setDraft(null); setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally { setBusy(false); }
  };

  const remove = async (id: string): Promise<void> => {
    setBusy(true);
    try { await api.deleteStrategy(id); await load(); }
    finally { setBusy(false); }
  };

  if (error && !data) return <div className="notice error">{error}</div>;
  if (!data) return <div className="dim">Loading…</div>;

  // Ranked worst-first by the API. The order is the point of the screen.
  const ordered = data.ranking
    .map((id) => data.strategies.find((s) => s.id === id))
    .filter((s): s is StrategyDto => Boolean(s));

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Strategies</h2>
        <div className="account-picker">
          {data.unassignedTradeCount > 0 && (
            <span className="dim">
              {data.unassignedTradeCount} trade{data.unassignedTradeCount === 1 ? '' : 's'} not
              attached to a strategy
            </span>
          )}
          <button
            className="btn btn-primary compact"
            onClick={() => { setDraft({ ...BLANK }); setEditing(null); }}
          >
            New strategy
          </button>
        </div>
      </div>

      {error && <div className="notice error">{error}</div>}

      {draft && (
        <StrategyForm
          draft={draft} busy={busy} editing={Boolean(editing)}
          onChange={setDraft}
          onCancel={() => { setDraft(null); setEditing(null); }}
          onSave={() => void save()}
        />
      )}

      {ordered.length === 0 && !draft && (
        <EmptyState icon="analytics" title="No strategies yet">
          A strategy is a claim about an edge — what it is, and what would show it has
          gone. Naming them is what turns a journal into an argument you can settle.
        </EmptyState>
      )}

      <div className="strategy-list">
        {ordered.map((s) => (
          <StrategyCard
            key={s.id} s={s} busy={busy}
            onEdit={() => {
              setEditing(s.id);
              setDraft({
                name: s.name, invalidation: s.invalidation, hypothesis: s.hypothesis,
                entryRules: s.entryRules, exitRules: s.exitRules,
                status: s.status, minSample: s.minSample,
              });
            }}
            onDelete={() => void remove(s.id)}
          />
        ))}
      </div>
    </div>
  );
}

function StrategyCard(
  { s, busy, onEdit, onDelete }:
  { s: StrategyDto; busy: boolean; onEdit: () => void; onDelete: () => void },
): JSX.Element {
  const p = s.performance;
  return (
    <article className={`strategy${p?.reviewSuggested ? ' needs-review' : ''}`}>
      <header>
        <div>
          <h3>{s.name}</h3>
          <span className={`tag status-${s.status}`}>{s.status}</span>
          {p?.provisional && (
            <span className="tag provisional" title={`${p.tradesToSample} more closed trades before this settles`}>
              provisional
            </span>
          )}
        </div>
        <div className="strategy-actions">
          <button className="btn btn-quiet compact" onClick={onEdit} disabled={busy}>Edit</button>
          <button className="btn btn-quiet compact" onClick={onDelete} disabled={busy}>Delete</button>
        </div>
      </header>

      {s.hypothesis && <p className="strategy-hypothesis">{s.hypothesis}</p>}

      {/* The invalidation sits beside the numbers on purpose: the review flag is
          only useful next to the condition the trader said would falsify it. */}
      <p className="strategy-invalidation">
        <span className="label">Fails if</span> {s.invalidation}
      </p>

      {p && (
        <div className="strategy-stats">
          <Stat label="Trades" value={String(p.summary.tradeCount)} />
          <Stat label="Net" value={money(p.summary.netPnl)}
            tone={Number(p.summary.netPnl) >= 0 ? 'up' : 'down'} />
          <Stat label="Win rate" value={pct(p.summary.winRate)} />
          <Stat label="Expectancy" value={money(p.summary.expectancy)}
            tone={Number(p.summary.expectancy) >= 0 ? 'up' : 'down'} />
          {p.expectancyR && <Stat label="Per unit risk" value={`${Number(p.expectancyR).toFixed(2)}R`} />}
        </div>
      )}

      {p?.provisional && (
        <p className="strategy-note dim">
          {p.tradesToSample} more closed trade{p.tradesToSample === 1 ? '' : 's'} before these
          numbers settle. They are shown rather than hidden so nobody computes them by hand.
        </p>
      )}

      {p?.reviewSuggested && (
        <p className="strategy-note review">
          <Icon name="alerts" /> The sample has settled and expectancy is negative. Worth
          checking against what you said would show the edge is gone.
        </p>
      )}
    </article>
  );
}

const Stat = ({ label, value, tone }: { label: string; value: string; tone?: 'up' | 'down' }): JSX.Element => (
  <div className="stat">
    <span className="stat-label">{label}</span>
    <span className={`stat-value${tone ? ` ${tone}` : ''}`}>{value}</span>
  </div>
);

function StrategyForm(
  { draft, busy, editing, onChange, onCancel, onSave }: {
    draft: StrategyInput; busy: boolean; editing: boolean;
    onChange: (d: StrategyInput) => void; onCancel: () => void; onSave: () => void;
  },
): JSX.Element {
  const set = <K extends keyof StrategyInput>(k: K, v: StrategyInput[K]): void =>
    onChange({ ...draft, [k]: v });
  const ready = draft.name.trim().length > 0 && draft.invalidation.trim().length > 0;

  return (
    <form
      className="panel strategy-form"
      onSubmit={(e) => { e.preventDefault(); if (ready) onSave(); }}
    >
      <h3>{editing ? 'Edit strategy' : 'New strategy'}</h3>

      <label>
        <span>Name</span>
        <input value={draft.name} maxLength={80} required
          onChange={(e) => set('name', e.target.value)} placeholder="Opening range breakout" />
      </label>

      <label>
        <span>What edge does this claim?</span>
        <textarea value={draft.hypothesis ?? ''} rows={2} maxLength={2000}
          onChange={(e) => set('hypothesis', e.target.value)}
          placeholder="Momentum from the RTH open persists for the first 30 minutes." />
      </label>

      {/* Required, and the reason this is not a tag. Said plainly in the label
          rather than enforced silently and explained by a 400. */}
      <label>
        <span>What would show the edge is gone? <em>Required</em></span>
        <textarea value={draft.invalidation} rows={2} maxLength={2000} required
          onChange={(e) => set('invalidation', e.target.value)}
          placeholder="Two consecutive weeks where the open fails to hold." />
        <small className="dim">
          A strategy without this is a tag. It is what lets AEGIS tell you when the claim
          is worth revisiting instead of guessing on your behalf.
        </small>
      </label>

      <div className="form-row">
        <label>
          <span>Entry rules</span>
          <textarea value={draft.entryRules ?? ''} rows={2} maxLength={2000}
            onChange={(e) => set('entryRules', e.target.value)} />
        </label>
        <label>
          <span>Exit rules</span>
          <textarea value={draft.exitRules ?? ''} rows={2} maxLength={2000}
            onChange={(e) => set('exitRules', e.target.value)} />
        </label>
      </div>

      <div className="form-row">
        <label>
          <span>Status</span>
          <select value={draft.status} onChange={(e) => set('status', e.target.value as StrategyInput['status'])}>
            <option value="testing">Testing</option>
            <option value="active">Active</option>
            <option value="retired">Retired</option>
          </select>
        </label>
        <label>
          <span>Trades before the numbers settle</span>
          <input type="number" min={1} max={10000} value={draft.minSample ?? 30}
            onChange={(e) => set('minSample', Number(e.target.value))} />
          <small className="dim">
            Thirty is a starting point, not a rule. A strategy that wins one time in five
            needs far more before its average means anything.
          </small>
        </label>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-quiet" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={!ready || busy}>
          {busy ? 'Saving…' : editing ? 'Save changes' : 'Create strategy'}
        </button>
      </div>
    </form>
  );
}
